import React, {useCallback, useState} from 'react';
import Button from "../../../components/ui/Button/Button";
import {NewChatData, useNewChatStore} from "../../../store";
import Input from "../../../components/ui/Input/Input";
import {new_chat_create} from "../../../api/chat_api";
import {isAlreadyToasted} from "../../../api/instance";
import {useLoaderStore} from "../../../store/loader/loader.store";
import {imageCompressor} from "../../../services/imageCopressor";
import {toast} from "react-toastify";
import {auth_get_profile} from "../../../api/auth_api";
import {useProfileStore} from "../../../store/profile/pofile.store";
import {useNavigate} from "react-router-dom";
import ConfirmWindow from "../../../components/layout/ConfirmWindow/ConfirmWindow";
import {AudioWaveform} from "../../../components/ui/AudioWaveform/AudioWaveform ";
import {Camera, Mic, AlertCircle, CheckCircle2, ArrowRight} from "lucide-react";
import StepAside from "../StepAside";
import {useObjectUrl} from "../../../hooks/useObjectUrl";

const MIN_VOICE_SECONDS = 10;

const Step4 = () => {

    const {currentStep, setStep, newChatData, updateChatData, resetChat} = useNewChatStore();
    const {showLoader, hideLoader, status} = useLoaderStore();
    const {updateUser} = useProfileStore();
    const [confirmModal, setConfirmModal] = useState(false);
    const navigate = useNavigate();
    const [seconds, setSeconds] = useState<number>(0);
    // The browser can't play the chosen recording, so its length can't be measured either
    const [voiceUnplayable, setVoiceUnplayable] = useState(false);
    const markVoiceUnplayable = useCallback(() => setVoiceUnplayable(true), []);
    const avatarUrl = useObjectUrl(newChatData.chat_avatar);
    const voiceUrl = useObjectUrl(newChatData.voice_sample);

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        key: "chat_avatar" | "voice_sample"
    ) => {
        const file = e.target.files?.[0] ?? null;
        if (key === 'voice_sample') {
            setSeconds(0);
            setVoiceUnplayable(false);
        }
        updateChatData({[key]: file});
    };

    const submitCreatingTheChat = async () => {
        try {
            showLoader("Creating digital clone...");
            const {chat_avatar, voice_sample} = newChatData;
            if (!chat_avatar || !voice_sample) return;
            // 1. Compress image
            const compressedImage = await imageCompressor(chat_avatar);
            const dataToSend: NewChatData = {
                ...newChatData,
                chat_avatar: compressedImage,
            };
            // 2. Create chat
            await new_chat_create(dataToSend);
            toast("Chat successfully created!", {type: "success"});
            // 3. Update profile
            const profile = await auth_get_profile();
            updateUser(profile.data);
            // 4. Reset and redirect
            resetChat();
            navigate("/");
        } catch (error: any) {
            console.error("submitCreatingTheChat error:", error);
            // Plan limit and unverified email errors already show a toast from the axios interceptor
            if (!isAlreadyToasted(error)) {
                toast(error?.response?.data?.message || "Failed to create chat", {type: "error"});
            }
            setConfirmModal(false);
        } finally {
            hideLoader();
        }
    };

    const voiceTooShort = Boolean(newChatData.voice_sample) && seconds > 0 && seconds < MIN_VOICE_SECONDS;

    const isButtonDisabled = () => {
        if (status) {
            return true
        }
        if (seconds < MIN_VOICE_SECONDS) {
            return true
        }
        return !(newChatData.chat_avatar && newChatData.voice_sample);
    }

    return (
        <div className="StepLayout">

            {
                confirmModal &&
                <ConfirmWindow closeMenu={() => setConfirmModal(false)} confirmAction={submitCreatingTheChat}
                               newChatData={newChatData}/>
            }

            <div className="StepLayout__left">

                <span className="eyebrow">Step {currentStep} of 4</span>
                <h2>Add a face and a voice</h2>
                <p className="StepLayout__description">A photo for the chat, and a voice message so replies can be
                    spoken in their voice.</p>

                <div className="StepLayout__field">
                    <span className="StepLayout__label">Photo</span>
                    <label className="StepLayout__avatarPicker">
                        <Input hidden onChange={(e) => handleFileChange(e, 'chat_avatar')} accept={'image/*'}
                               file={true}
                               title={''}
                               type={'file'}/>
                        <span className="StepLayout__avatarPreview">
                            {avatarUrl ? <img src={avatarUrl} alt=""/> : <Camera size={26}/>}
                        </span>
                        <span className="StepLayout__avatarText">
                            <b>{avatarUrl ? 'Change photo' : 'Upload a photo'}</b>
                            <span>JPG or PNG, a clear photo of their face works best</span>
                        </span>
                    </label>
                </div>

                <div className="StepLayout__field">
                    <span className="StepLayout__label">Voice sample</span>
                    <label className={`file-selector ${newChatData.voice_sample ? 'file-selector--done' : ''}`}>
                        <Input hidden onChange={(e) => handleFileChange(e, 'voice_sample')}
                               accept={'.m4a,.mp3,.wav,.ogg,.opus,audio/mpeg,audio/mp4,audio/ogg,audio/opus'} file={true}
                               title={newChatData.voice_sample ? 'Click to choose another recording'
                                   : `Upload a voice message (at least ${MIN_VOICE_SECONDS} seconds)`}
                               type={'file'}/>
                        <span className="icon-tile"><Mic size={20}/></span>
                        {
                            newChatData.voice_sample &&
                            <span className="file-selector__name">{newChatData.voice_sample.name}</span>
                        }
                    </label>

                    {
                        voiceUrl && <div className="StepLayout__voicePreview">
                            <AudioWaveform setSeconds={setSeconds} audioSrc={voiceUrl} onUnplayable={markVoiceUnplayable}/>
                        </div>
                    }

                    {
                        voiceUnplayable ?
                            <span className="StepLayout__status StepLayout__status--error">
                                <AlertCircle size={14}/> This browser can't play this file format. Please upload the
                                recording as .mp3 or .m4a, or try another browser.
                            </span>
                            : voiceTooShort ?
                            <span className="StepLayout__status StepLayout__status--error">
                                <AlertCircle size={14}/> The recording is {Math.round(seconds)}s. Please use one that's
                                at least {MIN_VOICE_SECONDS} seconds long.
                            </span>
                            : seconds >= MIN_VOICE_SECONDS &&
                            <span className="StepLayout__status StepLayout__status--ok">
                                <CheckCircle2 size={14}/> Great, {Math.round(seconds)} seconds of audio
                            </span>
                    }
                </div>

                <div className="StepLayout__buttons">
                    <Button type={'button'} onClick={() => setStep(currentStep - 1)} variant={'secondary'}
                            title={'Back'}/>
                    <Button type={'button'} disabled={isButtonDisabled()} onClick={() => setConfirmModal(true)}
                            variant={'primary'} title={'Review'} icon={<ArrowRight size={16}/>}/>
                </div>

            </div>

            <StepAside title="For the best voice">
                Use a recording where only this person speaks, with little background noise or music. 30 seconds or
                more sounds even more natural.
            </StepAside>
        </div>
    );
};

export default Step4;
